import {
  ImportMergeFieldDto,
  MergeFieldDto,
  QueryFactory,
} from '@lmt/shared-types';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { ChoosePrefix } from '@lmt/unicorn-shared/app/components/documents/components/ChoosePrefix';
import { MergeFieldGroupDto } from '@lmt/unicorn-shared/app/components/documents/dto/MergeFieldGroupDto';
import {
  GlobalFormsContext,
  useGlobalForms,
} from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { convertArrayToDictionary } from '@lmt/unicorn-shared/app/helpers/api-helper';
import {
  readFileAsExcel,
  triggerDownloadFileDialog,
} from '@lmt/unicorn-shared/app/helpers/file-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import ExportIcon from '@lmt/unicorn-shared/app/icons/docs/export-merge-field.svg?react';
import ImportIcon from '@lmt/unicorn-shared/app/icons/docs/import-merge-field.svg?react';
import CopyIcon from '@lmt/unicorn-shared/app/icons/documents-copy.svg?react';
import DeleteIcon from '@lmt/unicorn-shared/app/icons/documents-delete.svg?react';
import EditIcon from '@lmt/unicorn-shared/app/icons/documents-edit.svg?react';
import ViewIcon from '@lmt/unicorn-shared/app/icons/documents-show.svg?react';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-24.svg?react';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query'; // optional
import clsx from 'clsx';
import Logger from 'js-logger';
import {
  bindMenu,
  bindToggle,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { UseTranslationResponse } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import { DataGrid } from '../../common/dataGrid/DataGrid';
import { isEmpty } from '../../measurements/copy-datablock-field/common';
import {
  getMergeFieldsGroups,
  mergeFieldCodeAsString,
  useOnClone,
} from './helpers';
import styles from './MergeFieldList.module.scss';

const ImportIcon2: any = ImportIcon;
const ExportIcon2: any = ExportIcon;

const TableKeys: { [dictionary: string]: string } = {
  name: 'name',
  code: 'code',
  buttons: 'buttons',
};

const RowButtons = (props: {
  isOnlyView: boolean;
  row: QueryFactory.MergeFieldDto;
  onDelete: (mergeField: MergeFieldDto) => Promise<void>;
  onClone: (mergeField: MergeFieldDto) => Promise<void>;
  globalForms: GlobalFormsContext;
}) => {
  const { row, onDelete, onClone, globalForms, isOnlyView } = props;
  return (
    <div className={styles.buttonsContainer}>
      {row.isDefault || isOnlyView ? (
        <ViewIcon
          className={styles.editIcon}
          onClick={() => {
            globalForms.editMergeField(row.id);
          }}
          data-test-id="edit-merge-field-button"
        />
      ) : (
        <EditIcon
          className={styles.editIcon}
          onClick={() => {
            globalForms.editMergeField(row.id);
          }}
          data-test-id="edit-merge-field-button"
        />
      )}
      {!isOnlyView && (
        <CopyIcon
          className={styles.editIcon}
          onClick={() => {
            onClone(row).catch((e) => Logger.error(e));
          }}
          data-test-id="copy-merge-field-button"
        />
      )}
      {!isOnlyView && (
        <DeleteIcon
          className={styles.deleteIcon}
          onClick={() => {
            if (!row.isDefault) {
              onDelete(row).catch((e) => Logger.error(e));
            }
          }}
          data-disabled={row.isDefault}
          data-test-id="delete-merge-field-button"
        />
      )}
    </div>
  );
};

type Props = {
  onInsertMergeField: (mergeField: MergeFieldDto) => void;
};
export const MergeFieldList: React.FC<Props> = React.memo(
  function MergeFieldList(props) {
    const claims = useClaims();
    const modals = useModal();
    const globalForms = useGlobalForms();
    const i18n = useScopedTranslation('documents');
    const i18next = useScopedTranslation('documents.query_builder');
    const mergeFieldsQuery = QueryFactory.MergeFieldQuery.useGetQuery();
    const mergeFields = mergeFieldsQuery.data;
    const queryClient = useQueryClient();

    const [searchText, setSearchText] = useState('');
    const [searchTextDebounced] = useDebounce(searchText, 300);

    const onDelete = useEventCallback(async (mergeField: MergeFieldDto) => {
      if (
        !(await modals.showDeletionConfirmation({
          text: i18n.t('delete_merge_field_form_text', {
            name: mergeField.name,
          }),
          title: i18n.t('delete_merge_field_form_title'),
        }))
      ) {
        return;
      }

      try {
        await QueryFactory.MergeFieldClient.remove(mergeField.id);
        await mergeFieldsQuery.refetch();
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    });

    const dataBlockTypes = QueryFactory.DataBlockTypeQuery.useGetQuery({
      select: convertArrayToDictionary,
    });
    const exportMergeFields = useCallback(
      async (prefix: string) => {
        if (!mergeFields || !dataBlockTypes.data) return;

        const result = await exportToExcelFile(mergeFields, prefix);
        await triggerDownloadFileDialog(new Blob([result]), 'MergeFields.xlsx');
      },
      [mergeFields, dataBlockTypes.data],
    );

    const importExcelFileRef = useRef<HTMLInputElement>(null);
    const [loadingTitle, setLoadingTitle] = useState<string | undefined>(
      undefined,
    );

    const importMergeFields = useCallback(
      async (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (!dataBlockTypes.data) return;
        try {
          setLoadingTitle(i18n.t('import_loading'));
          const mergeFields = await parseExcelFile(ev, i18n as any);
          const hasDefaultPrefix = mergeFields.some(
            (x) => x.prefix?.toUpperCase() === 'X',
          );
          const promises = mergeFields
            .filter((x) => x.prefix?.toUpperCase() !== 'X')
            .map((mergeField) => {
              return QueryFactory.MergeFieldClient.import_(mergeField);
            });
          if (isEmpty(promises)) {
            if (hasDefaultPrefix) {
              toast(
                [
                  i18n.t('x_prefix_fields_import'),
                  i18n.t('no_data_to_import_toast'),
                ].join(' '),
                {
                  type: 'warning',
                },
              );
            } else {
              toast(i18n.t('no_data_to_import_toast'), {
                type: 'warning',
              });
            }
            return;
          }
          await Promise.all(promises);
          await mergeFieldsQuery.refetch();
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.MergeFieldQuery.getSingleQueryKey(
              undefined!,
            ),
          });
          if (hasDefaultPrefix) {
            toast(i18n.t('x_prefix_fields_import'), {
              type: 'warning',
            });
          }
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setLoadingTitle(undefined);
          // to allow choosing the same file again
          importExcelFileRef.current!.value = '';
        }
      },
      [dataBlockTypes.data],
    );
    const rowClick = useCallback((row: MergeFieldDto) => {
      if ('items' in (row as MergeFieldGroupDto)) {
        return;
      }
      if ('expression' in (row as MergeFieldDto)) {
        props.onInsertMergeField(row);
      }
    }, []);

    const groupedFields = useMemo(
      () => getMergeFieldsGroups(mergeFields ?? [], searchTextDebounced),
      [dataBlockTypes.data, mergeFields, searchTextDebounced],
    );

    const onClone = useOnClone();

    const popupState = usePopupState({
      variant: 'popper',
      popupId: 'merge-field',
    });

    const columns = useMemo(
      () => [
        {
          key: TableKeys.name,
          cellRender: (row: any) => (
            <div className={styles.cellName} title={row.data.name}>
              <div
                onClick={() => rowClick(row.data)}
                className={styles.fieldTitle}
              >
                {row.data.name}
              </div>
            </div>
          ),
        },
        {
          key: TableKeys.code,
          width: 73,
          cellRender: (row: any) => {
            const code = mergeFieldCodeAsString(row.data);
            return (
              <div className={styles.cellCode} title={code}>
                {code}
              </div>
            );
          },
        },
        {
          key: TableKeys.buttons,
          width: claims.permissions.mergeFieldManagement ? 70 : 30,
          cellRender: (cell: any) => (
            <div className={styles.cellButtons}>
              <RowButtons
                isOnlyView={!claims.permissions.mergeFieldManagement}
                row={cell.data}
                onDelete={onDelete}
                onClone={async (mergeField: MergeFieldDto) => {
                  await onClone(mergeField, {
                    onLoading: () => setLoadingTitle(i18next.t('copy_loading')),
                    onError: () => setLoadingTitle(undefined),
                    onFinish: () => setLoadingTitle(undefined),
                  });
                }}
                globalForms={globalForms}
              />
            </div>
          ),
        },
      ],
      [globalForms, claims.permissions],
    );

    const dataGridElement = useMemo(
      () => (
        <DataGrid
          key={groupedFields.length}
          columns={columns}
          dataSource={groupedFields}
          showColumnHeaders={false}
          scrolling={{
            mode: 'virtual',
            showScrollbar: 'always',
            useNative: true,
          }}
        />
      ),
      [groupedFields, globalForms],
    );

    return (
      <Loading loading={!!loadingTitle} title={loadingTitle}>
        <div
          className={styles.mergeFieldListWrapper}
          data-test-id="merge-field-list"
        >
          <div className={styles.header}>
            <span>
              {(i18n.t('fields_header_title') as string).toUpperCase()}
            </span>
            {claims.permissions.mergeFieldManagement && (
              <div className={'flexRow'}>
                <AppPopper {...bindMenu(popupState)} placement={'bottom'}>
                  <ChoosePrefix
                    onClose={(prefix) => {
                      popupState.close();
                      if (prefix) {
                        void exportMergeFields(prefix);
                      }
                    }}
                  />
                </AppPopper>
                <div {...bindToggle(popupState)}>
                  <ExportIcon2
                    className={clsx(
                      styles.icon,
                      popupState.isOpen
                        ? styles.iconPressed
                        : styles.iconUnpressed,
                    )}
                    testId={'exportFields'}
                    title={i18n.t('export_button_title')}
                  />
                </div>
                <ImportIcon2
                  className={clsx(styles.icon, styles.iconUnpressed)}
                  onClick={() => importExcelFileRef.current!.click()}
                  title={i18n.t('import_button_title')}
                  testId={'importFields'}
                />
                <input
                  type="file"
                  id="importExcelFile"
                  ref={importExcelFileRef}
                  className={'hidden'}
                  onChange={importMergeFields}
                />
                <IconButton
                  color="blue"
                  className={styles.icon}
                  onClick={globalForms.addMergeField}
                  iconComponent={<AddIcon />}
                  testId={'addMergeField'}
                  title={i18n.t('add_new_field_button_title')}
                />
              </div>
            )}
          </div>
          <div className={styles.search}>
            <SearchInput
              value={searchText}
              placeholder={i18n.t('search_panel_placeholder')}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className={styles.container}>{dataGridElement}</div>
        </div>
      </Loading>
    );
  },
);

async function exportToExcelFile(mergeFields: MergeFieldDto[], prefix: string) {
  return import('exceljs').then(async (excelJs) => {
    const workbook = new excelJs.Workbook();
    const worksheet = workbook.addWorksheet('Sheet');
    worksheet.columns = [
      { header: 'Code', key: 'fullCode', width: 8 },
      { header: 'Name', key: 'name', width: 40 },
      { header: 'Expression', key: 'expression', width: 100 },
    ];

    mergeFields = mergeFields.filter((x) => x.prefix === prefix);
    mergeFields.forEach((mergeField) => {
      (mergeField as any).fullCode = mergeFieldCodeAsString(mergeField);
    });
    worksheet.addRows(mergeFields);

    worksheet.columns.forEach((_column, index) => {
      const cell = worksheet.getCell(1, index + 1);
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: 'center',
      };
    });
    return await workbook.xlsx.writeBuffer();
  });
}

async function parseExcelFile(
  e: React.ChangeEvent<HTMLInputElement>,
  i18n: UseTranslationResponse<'translation', 'documents'>,
): Promise<ImportMergeFieldDto[]> {
  const file = e.target!.files![0];
  if (!file) {
    return [];
  }
  const values = await readFileAsExcel(file);
  const importedMergeFields: ImportMergeFieldDto[] = [];

  for (let i = 2; i < values.length; i++) {
    const row = values[i] as unknown[];
    try {
      if (!row?.length) continue;
      const typedRow = parseRowFromExcel(i, row, i18n);
      importedMergeFields.push(typedRow);
    } catch (e) {
      throw e;
    }
  }
  return importedMergeFields;
}

function parseRowFromExcel(
  index: number,
  row: unknown[],
  i18n: UseTranslationResponse<'translation', 'documents'>,
): ImportMergeFieldDto {
  const fullCode = (row[1] as string).toString();
  const matchResult = fullCode.match(
    /^(?<prefix>[a-zA-Z]{1,3})(?<code>\d{1,6})$/,
  );
  if (!matchResult) {
    throw new Error(
      i18n.t('errors.wrong_code_format', { code: fullCode, line: index }),
    );
  }

  const prefix = matchResult?.groups?.prefix ?? '';
  const code = parseInt(matchResult?.groups?.code ?? '');

  return {
    code: code,
    prefix: prefix?.toString(),
    name: (row[2] as string)?.toString() || '',
    expression: (row[3] as string)?.toString() || '',
  };
}
